import { useState } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch("https://readdy.ai/api/form/d6sp6p837h4e5cfhpsc0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setEmail("");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer id="contact" className="bg-[#111111] text-white">
      {/* Main grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex flex-col leading-none mb-4">
              <span
                className="text-[11px] tracking-[0.25em] uppercase font-bold text-white/60"
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                The Philadelphia
              </span>
              <span
                className="text-[22px] tracking-[0.12em] uppercase font-black leading-tight text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Sandwich Co.
              </span>
            </div>
            <p className="text-white/50 text-xs leading-relaxed mb-5" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Handcrafted sandwiches rooted in Philadelphia tradition since 2012.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://instagram.com/phillysandoco" rel="nofollow" target="_blank" aria-label="Instagram" className="w-8 h-8 flex items-center justify-center border border-white/20 rounded-full hover:border-white/60 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-sm text-white/70"></i>
              </a>
              <a href="https://facebook.com" rel="nofollow" target="_blank" aria-label="Facebook" className="w-8 h-8 flex items-center justify-center border border-white/20 rounded-full hover:border-white/60 transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-sm text-white/70"></i>
              </a>
              <a href="https://twitter.com" rel="nofollow" target="_blank" aria-label="Twitter" className="w-8 h-8 flex items-center justify-center border border-white/20 rounded-full hover:border-white/60 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-sm text-white/70"></i>
              </a>
            </div>
          </div>

          {/* Visit Us */}
          <div>
            <h4 className="text-white text-xs tracking-[0.2em] uppercase font-bold mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>
              <a id="visit-us">Visit Us</a>
            </h4>
            <ul className="space-y-2.5">
              {["1234 South Street, Philadelphia, PA", "Mon–Fri: 10am – 9pm", "Sat–Sun: 9am – 10pm", "(215) 555-0182"].map((item) => (
                <li key={item} className="text-white/50 text-xs leading-relaxed" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-white text-xs tracking-[0.2em] uppercase font-bold mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>
              <a id="explore">Explore</a>
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: "Menu", href: "#menu" },
                { label: "Our Story", href: "#story" },
                { label: "Blog", href: "#blog" },
                { label: "Contact", href: "#contact" },
              ].map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    onClick={(e) => handleAnchorClick(e, item.href)}
                    className="text-white/50 text-xs hover:text-white/80 transition-colors cursor-pointer"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-white text-xs tracking-[0.2em] uppercase font-bold mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>
              <a id="connect">Connect</a>
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: "Catering Inquiries", href: "#contact" },
                { label: "Press & Media", href: "#contact" },
                { label: "Wholesale", href: "#contact" },
                { label: "Careers", href: "#contact" },
              ].map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    onClick={(e) => handleAnchorClick(e, item.href)}
                    className="text-white/50 text-xs hover:text-white/80 transition-colors cursor-pointer"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="border-t border-white/10 mt-12 pt-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p
                className="text-white font-bold text-lg mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Stay in the loop.
              </p>
              <p className="text-white/50 text-xs" style={{ fontFamily: "'Barlow', sans-serif" }}>
                New menu drops, limited merch &amp; stories from the counter.
              </p>
            </div>
            {submitted ? (
              <p className="text-white/70 text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>
                You&apos;re on the list. See you soon.
              </p>
            ) : (
              <form
                data-readdy-form
                id="newsletter-form"
                onSubmit={handleSubmit}
                className="flex items-center gap-0 w-full md:w-auto"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="bg-white/10 border border-white/20 border-r-0 text-white placeholder-white/30 text-sm px-4 py-3 outline-none focus:border-white/50 transition-colors w-64 rounded-l-md"
                  style={{ fontFamily: "'Barlow', sans-serif" }}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-white text-black text-xs tracking-[0.15em] uppercase font-bold px-5 py-3 hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer rounded-r-md"
                  style={{ fontFamily: "'Barlow', sans-serif" }}
                >
                  {submitting ? "..." : "Subscribe"}
                </button>
              </form>
            )}
          </div>
          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/30 text-xs" style={{ fontFamily: "'Barlow', sans-serif" }}>
            &copy; 2026 The Philadelphia Sandwich Company. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {["Privacy Policy", "Terms of Use"].map((item) => (
              <a key={item} href="#" className="text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer" style={{ fontFamily: "'Barlow', sans-serif" }}>
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
